"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FieldMappingEditor = void 0;
var react_1 = __importDefault(require("react"));
var Button_1 = require("office-ui-fabric-react/lib/Button");
var Dropdown_1 = require("office-ui-fabric-react/lib/Dropdown");
var Text_1 = require("office-ui-fabric-react/lib/Text");
var TextField_1 = require("office-ui-fabric-react/lib/TextField");
var Toggle_1 = require("office-ui-fabric-react/lib/Toggle");
var ai_hub_settings_css_1 = require("../utils/ai-hub-settings-css");
var RecordEntryRow_1 = require("./RecordEntryRow");
/**
 * Editor for a single field mapping.
 *
 * All change indicators come from the memoized `changeFlags` object so the
 * component’s prop identity is stable across renders that don't touch this
 * mapping — letting `React.memo` skip them.
 */
var FieldMappingEditorComponent = function (props) {
    var fieldId = props.fieldId, mapping = props.mapping, mappingId = props.mappingId, mappingIndex = props.mappingIndex, systemOptions = props.systemOptions, changeFlags = props.changeFlags, onMappingChange = props.onMappingChange, onRemoveMapping = props.onRemoveMapping;
    var isValueChanged = function (key) {
        return changeFlags.isMappingValueChanged(fieldId, mappingId, key);
    };
    var isValueSetChanged = function (key) {
        return changeFlags.isMappingValueSetChanged(fieldId, mappingId, key);
    };
    var isConfigChanged = function (key) { return changeFlags.isMappingConfigChanged(fieldId, mappingId, key); };
    var isMapEntryChanged = function (entryIndex, part) {
        return changeFlags.isMappingMapEntryChanged(fieldId, mappingId, entryIndex, part);
    };
    // Single-emit helpers keep the JSX free of `{ type, fieldId, mappingId }`
    // boilerplate while the dispatch boundary stays a pure typed action.
    var emit = function (action) {
        return onMappingChange(fieldId, mappingId, action);
    };
    var mapEntries = Object.entries(mapping.map || {});
    return (react_1.default.createElement("div", { className: "ws-ai-hub-settings-mapping-card" },
        react_1.default.createElement("div", { className: "ws-ai-hub-settings-mapping-header" },
            react_1.default.createElement(Text_1.Text, { variant: "medium" },
                "Mapping ",
                mappingIndex + 1),
            react_1.default.createElement(Button_1.IconButton, { className: "ws-admin-button-hover-danger", iconProps: { iconName: "Delete" }, title: "Remove mapping", ariaLabel: "Remove mapping", onClick: function () {
                    void onRemoveMapping(fieldId, mappingId);
                } })),
        react_1.default.createElement("div", { className: "ws-ai-hub-settings-two-column" },
            react_1.default.createElement(Dropdown_1.Dropdown, { className: ai_hub_settings_css_1.inputChangedClass(isValueChanged("system")), label: "Source system", selectedKey: mapping.system || undefined, options: systemOptions, onChange: function (_, option) {
                    emit({
                        kind: "value",
                        key: "system",
                        value: (option && String(option.key)) || "",
                    });
                } }),
            react_1.default.createElement(TextField_1.TextField, { className: ai_hub_settings_css_1.inputChangedClass(isValueChanged("field")), label: "Mapped field", value: mapping.field || "", maxLength: 256, onChange: function (_, nextValue) {
                    emit({ kind: "value", key: "field", value: nextValue || "" });
                } }),
            react_1.default.createElement(TextField_1.TextField, { className: ai_hub_settings_css_1.inputChangedClass(isValueChanged("defaultValue")), label: "Default value", value: mapping.defaultValue || "", maxLength: 4000, onChange: function (_, nextValue) {
                    emit({
                        kind: "value",
                        key: "defaultValue",
                        value: nextValue || "",
                    });
                } })),
        react_1.default.createElement("div", { className: "ws-ai-hub-settings-panel-section ws-ai-hub-settings-mapping-value-map-section" },
            react_1.default.createElement("div", { className: "ws-ai-hub-settings-toolbar" },
                react_1.default.createElement(Text_1.Text, { className: "ws-ai-hub-settings-panel-section-title" }, "Value map"),
                react_1.default.createElement(Button_1.DefaultButton, { className: "ws-admin-button-add ws-admin-button-hover-accent", text: "Add Map Entry", onClick: function () {
                        emit({ kind: "appendMapEntry", defaultKeyPrefix: "sourceValue" });
                    } })),
            mapEntries.length > 0 && (react_1.default.createElement("div", { className: "ws-ai-hub-settings-record-row-header" },
                react_1.default.createElement(Text_1.Text, { className: "ws-ai-hub-settings-summary-label", variant: "small" }, "Source value"),
                react_1.default.createElement(Text_1.Text, { className: "ws-ai-hub-settings-summary-label", variant: "small" }, "Target value"),
                react_1.default.createElement("span", null))),
            mapEntries.map(function (_a, entryIndex) {
                var entryKey = _a[0], entryValue = _a[1];
                return (react_1.default.createElement(RecordEntryRow_1.RecordEntryRow, { key: entryIndex, entryKey: entryKey, entryValue: entryValue, entryIndex: entryIndex, keyAriaLabel: "Source value", valueAriaLabel: "Target value", isKeyChanged: isMapEntryChanged(entryIndex, "key"), isValueChanged: isMapEntryChanged(entryIndex, "value"), removeButtonTitle: "Remove map entry", removeButtonAriaLabel: "Remove map entry", onKeyChange: function (nextKey) {
                        emit({
                            kind: "updateMapEntry",
                            entryIndex: entryIndex,
                            key: nextKey,
                            value: entryValue,
                        });
                    }, onValueChange: function (nextValue) {
                        emit({
                            kind: "updateMapEntry",
                            entryIndex: entryIndex,
                            key: entryKey,
                            value: nextValue,
                        });
                    }, onRemove: function () {
                        emit({ kind: "removeMapEntry", entryIndex: entryIndex });
                    } }));
            })),
        react_1.default.createElement("div", { className: "ws-ai-hub-settings-two-column" },
            react_1.default.createElement(Toggle_1.Toggle, { className: ai_hub_settings_css_1.inputChangedClass(isValueSetChanged("append")), label: "Append to value set", checked: !!(mapping.valueSet && mapping.valueSet.append), onChange: function (_, checked) {
                    emit({ kind: "valueSetAppend", value: !!checked });
                } })),
        !!(mapping.valueSet && mapping.valueSet.append) && (react_1.default.createElement("div", { className: "ws-ai-hub-settings-two-column" },
            react_1.default.createElement(TextField_1.TextField, { className: ai_hub_settings_css_1.inputChangedClass(isValueSetChanged("format")), label: "Value set format", value: (mapping.valueSet && mapping.valueSet.format) || "", maxLength: 256, onChange: function (_, nextValue) {
                    emit({ kind: "valueSetFormat", value: nextValue || "" });
                } }),
            react_1.default.createElement(TextField_1.TextField, { className: ai_hub_settings_css_1.inputChangedClass(isConfigChanged("userNameSubField")), label: "User name sub-field", value: (mapping.config && mapping.config.userNameSubField) || "", maxLength: 256, onChange: function (_, nextValue) {
                    emit({
                        kind: "config",
                        key: "userNameSubField",
                        value: nextValue || "",
                    });
                } }),
            react_1.default.createElement(TextField_1.TextField, { className: ai_hub_settings_css_1.inputChangedClass(isConfigChanged("emailAddressSubField")), label: "Email sub-field", value: (mapping.config && mapping.config.emailAddressSubField) || "", maxLength: 256, onChange: function (_, nextValue) {
                    emit({
                        kind: "config",
                        key: "emailAddressSubField",
                        value: nextValue || "",
                    });
                } })))));
};
exports.FieldMappingEditor = react_1.default.memo(FieldMappingEditorComponent);
